import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Fragment } from 'react';
import { CgClose, CgDanger } from 'react-icons/cg';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { SpiritoryModalProps } from '@/types/props/components';
import translationFallback from '@/utilites/translations/translationFallback';
import { PrimaryButton } from '../PrimaryButton';

export default function SpiritoryModal({
  showModal,
  cancelAction,
  submitAction,
  cancelText,
  submitText,
  title,
  body,
  submitActionDisabled = false,
  cancelActionDisabled = false,
  dialogClassName,
  contentClassName,
  containerClassName,
  showButtons = true,
  showCancelButton = true,
  showSubmitButton = true,
  type = 'normal',
}: SpiritoryModalProps) {
  const { t: tCommon } = useTranslation('common');

  return (
    <Transition appear show={showModal} as={Fragment}>
      <Dialog
        as="div"
        className="tw-relative tw-z-[9999] max-nine:tw-p-2"
        onClose={() => {
          if (!cancelActionDisabled) cancelAction();
        }}
      >
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-60" />
        <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4">
          <TransitionChild
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0 tw-scale-95"
            enterTo="tw-opacity-100 tw-scale-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100 tw-scale-100"
            leaveTo="tw-opacity-0 tw-scale-95"
          >
            <DialogPanel
              className={clsx(
                'tw-bg-white tw-rounded-2xl tw-shadow-lg tw-max-w-lg tw-w-full',
                dialogClassName,
                containerClassName,
                contentClassName
              )}
            >
              {/* Header */}
              <div
                className={clsx(
                  'tw-bg-white tw-p-4 tw-rounded-t-2xl tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-b-gray-200 tw-border-b-0.75 tw-border-solid',
                  {
                    'tw-border-none': type === 'confirmation',
                  }
                )}
              >
                <DialogTitle className="tw-font-bold tw-text-black tw-text-lg tw-border-b-0 tw-flex tw-items-center tw-mb-0">
                  {type === 'alert' ? (
                    <>
                      <CgDanger className="tw-text-red-500 tw-mr-2" />
                      {tCommon('alert')}
                    </>
                  ) : (
                    title
                  )}
                  {cancelAction && (
                    <CgClose
                      className="tw-ml-auto tw-cursor-pointer tw-text-gray-500 tw-text-xl"
                      onClick={cancelAction}
                    />
                  )}
                </DialogTitle>
              </div>

              {/* Body */}
              {(type === 'normal' || type === 'info' || type === 'alert') && (
                <div className="tw-px-6 tw-py-4 tw-text-gray-700">{body}</div>
              )}

              {/* Footer */}
              {(type === 'info' || type === 'alert' || showButtons) && (
                <div
                  className={clsx(
                    'tw-border-t-gray-200 tw-border-r-0 tw-border-l-0 tw-border-b-0 tw-border-t-0.75 tw-border-solid tw-flex tw-justify-end tw-py-3 tw-px-4 tw-rounded-b-2xl',
                    {
                      'tw-border-none': type === 'confirmation',
                    }
                  )}
                >
                  <div className="tw-flex tw-gap-2">
                    {showCancelButton && (
                      <PrimaryButton
                        outlined={true}
                        onClick={cancelAction}
                        disabled={cancelActionDisabled}
                      >
                        {cancelText ||
                          translationFallback(
                            'cancel',
                            tCommon('cancel'),
                            'Cancel'
                          )}
                      </PrimaryButton>
                    )}
                    {showSubmitButton && (
                      <PrimaryButton
                        onClick={submitAction}
                        disabled={submitActionDisabled}
                      >
                        {submitText ||
                          translationFallback(
                            'confirm',
                            tCommon('confirm'),
                            'Confirm'
                          )}
                      </PrimaryButton>
                    )}
                  </div>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
